import axios from 'axios';
import Cookies from 'js-cookie';
import { encodeWithKeyAndShift, decodeWithKeyAndShift } from './base64Utils';
import Auth from './auth';
import endpoints from './../config/endpoints';
import { store } from '@store/index';
import { clearServiceRuleData } from '@store/actions/serviceRuleMasterActions';
import { errorLogger, warningLogger } from './applicationLogUtils';
import { getIsRefreshing, setIsRefreshing, processQueue } from './tokenRefreshState';
import { toast } from 'react-toastify';
import { performRefreshWithRetry } from './refreshTokenService';
import { showAuthServiceDownModal } from '../auth/authErrorPresenter';
import { parseApiError } from '../auth/errorContract';

/**
 * ProactiveTokenRefresh Service
 * Manages background token refresh process to prevent token expiration
 * Refreshes token proactively based on expiration time (default: 5 minutes before expiry)
 */
class ProactiveTokenRefresh {
    private refreshTimer: NodeJS.Timeout | null = null;
    private readonly REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minute before expiry

    /**
     * Start proactive token refresh process
     * @param expiryTime - Token expiry time in milliseconds (Unix timestamp)
     */
    startProactiveRefresh(expiryTime: number): void {
        console.log(
            '[TEST] startProactiveRefresh called with expiry:',
            new Date(expiryTime).toLocaleTimeString()
        );
        // Clear any existing timer
        this.stopProactiveRefresh();

        const now = new Date().getTime();
        const timeUntilExpiry = expiryTime - now;

        // Only start if token has more than buffer time remaining
        if (timeUntilExpiry <= 0) {
            warningLogger('Token already expired or expiry time is invalid');
            return;
        }

        // Calculate when to refresh (buffer time before actual expiry)
        const refreshTime = Math.max(timeUntilExpiry - this.REFRESH_BUFFER_MS, 1000); // Min 1 second

        console.log(
            `[Token Refresh] Scheduled refresh in ${(refreshTime / 1000 / 60).toFixed(2)} minutes. ` +
            `Token expires in ${(timeUntilExpiry / 1000 / 60).toFixed(2)} minutes.`
        );

        // Set timer to trigger refresh
        this.refreshTimer = setTimeout(() => {
            this.performTokenRefresh();
        }, refreshTime);
    }

    /**
     * Stop proactive token refresh process
     */
    stopProactiveRefresh(): void {
        if (this.refreshTimer !== null) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
            console.log('[Token Refresh] Proactive refresh timer cleared');
        }
    }

    /**
     * Perform the actual token refresh
     */
    private async performTokenRefresh(): Promise<void> {
        // Prevent concurrent refresh attempts
        if (getIsRefreshing()) {
            warningLogger('[Token Refresh] Refresh already in progress, skipping duplicate attempt');
            return;
        }

        setIsRefreshing(true);
        console.log(`[Token Refresh] Proactive refresh starting at ${new Date().toLocaleTimeString()}`);

        try {
            const result = await performRefreshWithRetry();

            if (result.success) {
                const data = result.data;
                const milliseconds = data.auth_iam_status.expires_in * 1000;
                const newExpiryTime = new Date(new Date().getTime() + milliseconds).getTime();
                console.log(`[Token Refresh] ✓ Proactive refresh succeeded. New expiry: ${new Date(newExpiryTime).toLocaleTimeString()}. Token valid for ${(milliseconds / 60000).toFixed(2)} min`);
                const permissions = encodeWithKeyAndShift(JSON.stringify(data?.access_list ?? ''));
                const newToken = data.auth_iam_status.access_token;
                Auth.refreshToken(newExpiryTime, newToken, data.auth_iam_status.refresh_token, permissions, data.user_id, data.usertype_id);
                store.dispatch(clearServiceRuleData('hrp-rules'));
                processQueue(null, newToken);
            } else {
                errorLogger(`[Token Refresh] Proactive refresh failed after all retries`);
                processQueue(result.error, null);
                // Terminal proactive failure -> blocking modal; OK logs the user out.
                showAuthServiceDownModal(parseApiError(result.error)?.correlationId);
            }
        } finally {
            setIsRefreshing(false);
        }
    }

    /**
     * Restart refresh cycle (useful when tokens are manually updated)
     * @param expiryTime - New token expiry time in milliseconds
     */
    restartRefreshCycle(expiryTime: number): void {
        this.startProactiveRefresh(expiryTime);
    }

    /**
     * Check if refresh is currently in progress
     */
    isRefreshInProgress(): boolean {
        return getIsRefreshing();
    }
}

// Singleton instance
export const proactiveTokenRefresh = new ProactiveTokenRefresh();
//This resumes proactive refresh whenever the module is loaded after a browser reload.
if (typeof window !== 'undefined') {
    const savedExpiry = Number(Cookies.get('expiry'));
    if (Number.isFinite(savedExpiry) && savedExpiry > Date.now()) {
        proactiveTokenRefresh.startProactiveRefresh(savedExpiry);
    }
}
export default proactiveTokenRefresh;
